import { useEffect, useMemo, useRef, useState } from 'react'
import * as UtlHttp from '../../../../../services/api/https/patio/tomada-utilizacao.http'
import { errorHandler } from '../../../../../services/util/error.service'
import { parseToMysqlFormat } from '../../../../../services/util/date.service'
import * as CompanyHttp from '../../../../../services/api/https/settings/company.http'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  SAVE: 'Salvando informações da utilização',
  SUCCESS: 'Salvo com sucesso',
}

const MANDATORY_FIELDS = {
  TOMADA: 'Selecione a Tomada',
  DATA_INICIAL: 'Data inicial deve ser preenchida',
  HORA_INICIAL: 'Hora inicial deve ser preenchida',
  DATA_FINAL: 'Data final deve ser preenchida',
  HORA_FINAL: 'Hora final deve ser preenchida',
  PLACA: 'Placa deve ser preenchida',
  MOTORISTA: 'Motorista deve ser preenchido',
}

const INITIAL = {
  id: undefined,
  patio_tomada_id: null,
  _dataInicial: new Date(),
  _horaInicial: '',
  _dataFinal: null,
  _horaFinal: '',
  placa: '',
  motorista: '',
  rg: '',
  observacao: '',
  status: undefined,
  company_id: null,
}

const parseDateOnly = s => {
  if (!s) return null
  const d = new Date(s)
  if (!isNaN(d.getTime())) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }
  const str = String(s).split('T')[0]
  const parts = str.split('-').map(Number)
  if (parts.length === 3 && parts.every(n => !Number.isNaN(n))) {
    return new Date(parts[0], parts[1] - 1, parts[2])
  }
  return null
}

export default function useUtilizacaoModal() {
  const [state, setState] = useState({
    utl: { ...INITIAL },
    tomadas: [],
    companies: [],
    loadingMessage: LOAD_MESSAGE.NONE,
    errorMessage: null,
    instanceKey: 0,
  })

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const localDispatch = useMemo(() => {
    return {
      async start(utl, selectedTp, tomadasFromParent = []) {
        try {
          const tomadas = Array.isArray(tomadasFromParent) ? tomadasFromParent : []
          const companies = await CompanyHttp.getCompanies()

          if (utl) {
            const m = {
              ...INITIAL,
              ...utl,
              patio_tomada_id: utl.patio_tomada_id,
              _dataInicial: parseDateOnly(utl.data_inicial) || new Date(),
              _horaInicial: utl.hora_inicial ? utl.hora_inicial.substring(0, 5) : null,
              _dataFinal: parseDateOnly(utl.data_final),
              _horaFinal: utl.hora_final ? utl.hora_final.substring(0, 5) : null,
              placa: utl.placa || '',
              company_id: utl.company_id || null,
            }

            setState(prev => ({
              ...prev,
              tomadas,
              companies,
              utl: m,
              instanceKey: (prev.instanceKey || 0) + 1,
            }))
          } else {
            setState(prev => ({
              ...prev,
              tomadas,
              companies,
              utl: { ...INITIAL },
              instanceKey: (prev.instanceKey || 0) + 1,
            }))
          }
        } catch (err) {
          setState(prev => ({ ...prev, errorMessage: errorHandler(err) }))
        }
      },

      onChange(field, value) {
        setState(prev => ({ ...prev, utl: { ...prev.utl, [field]: value } }))
      },

      onBtnSalvarClicked(selectedTp) {
        const m = stateRef.current.utl
        if (!m.patio_tomada_id)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.TOMADA }))
        if (!m._dataInicial)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.DATA_INICIAL }))
        if (!m._horaInicial || m._horaInicial.length < 4)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.HORA_INICIAL }))
        if (!m.placa || m.placa.trim() === '')
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.PLACA }))
        if (!m.motorista || m.motorista.trim() === '')
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.MOTORISTA }))

        const toDateTime = (d, hhmm) => {
          if (!d || !hhmm) return null
          const [h, m] = hhmm.split(':').map(Number)
          const dt = new Date(d)
          dt.setHours(h || 0, m || 0, 0, 0)
          return dt
        }
        const mref = stateRef.current.utl
        const dtIn = toDateTime(mref._dataInicial, mref._horaInicial)
        const dtOut = toDateTime(mref._dataFinal, mref._horaFinal)
        if (dtIn && dtOut && dtIn.getTime() > dtOut.getTime()) {
          return setState(prev => ({
            ...prev,
            errorMessage: 'Data e Hora iniciais devem ser anteriores à Data e Hora finais',
          }))
        }

        setState(prev => ({ ...prev, errorMessage: null, loadingMessage: LOAD_MESSAGE.SAVE }))
      },

      async save(selectedTp) {
        try {
          const m = stateRef.current.utl

          const payload = {
            id: m.id,
            patio_tomada_id: m.patio_tomada_id,
            data_inicial: parseToMysqlFormat(m._dataInicial),
            hora_inicial:
              (m._horaInicial || '').length === 5 ? `${m._horaInicial}:00` : m._horaInicial || '',

            data_final: m._dataFinal ? parseToMysqlFormat(m._dataFinal) : null,
            hora_final:
              m._dataFinal && m._horaFinal
                ? m._horaFinal.length === 5
                  ? `${m._horaFinal}:00`
                  : m._horaFinal
                : null,

            placa: (m.placa || '').toUpperCase(),
            motorista: m.motorista,
            rg: m.rg || null,
            observacao: m.observacao || null,
            company_id: m.company_id || null,
          }

          const saved = await UtlHttp.saveUtilizacao(payload)
          setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.SUCCESS }))
          return saved
        } catch (err) {
          setState(prev => ({
            ...prev,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(err),
          }))
          throw err
        } finally {
          setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.NONE }))
        }
      },

      reset() {
        setState({
          utl: { ...INITIAL },
          tomadas: [],
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
          instanceKey: 0,
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
